import { ArrowShortLeftIcon, ArrowShortRightIcon } from '@fourthwall/icons'
import type { ChangeEvent, FormEvent } from 'react'
import React, { useEffect, useState } from 'react'
import { Input } from '../../inputs/Input'
import { Icon } from '../Icon'
import * as S from './Pagination.styled'
import type { PaginationProps } from './types'
import { validatePageNumber } from './utils'

export const Pagination = ({ pageCount, currentPage, total, onChange }: PaginationProps) => {
  const [inputValue, setInputValue] = useState(`${currentPage}`)
  const parsedInputValue = parseInt(inputValue)

  const handleSetInputValue = (value: string) => {
    if (value && /^[0-9]*$/.test(value)) {
      return setInputValue(value)
    }

    return setInputValue('')
  }

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleSetInputValue(e.target.value)
  }

  useEffect(() => {
    handleSetInputValue(`${currentPage}`)
  }, [currentPage])

  const submitInputChange = () => {
    if (parsedInputValue !== currentPage && validatePageNumber(parsedInputValue, pageCount)) {
      if (onChange) {
        onChange(parsedInputValue)
      }
    } else {
      setInputValue(`${currentPage}`)
    }
  }

  const handleInputBlur = () => {
    submitInputChange()
  }

  const handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    submitInputChange()
  }

  const handleIconClick = (nextPage: number) => () => {
    if (validatePageNumber(nextPage, pageCount)) {
      if (onChange) {
        onChange(nextPage)
      }
    }
  }

  const renderTotal = () => {
    if (!total) return null

    const from = currentPage * total.limit - (total.limit - 1)
    const to = currentPage === pageCount ? total.count : currentPage * total.limit

    return (
      <S.Total>
        Showing {from}-{to} of {total.count} results
      </S.Total>
    )
  }

  return (
    <S.Pagination data-testid="Pagination">
      {renderTotal()}
      <S.IconContainer
        $left
        $disabled={currentPage === 1}
        onClick={handleIconClick(currentPage - 1)}
        data-testid="Pagination.LeftIcon"
      >
        <Icon component={ArrowShortLeftIcon} height={16} />
      </S.IconContainer>
      <S.InputForm onSubmit={handleFormSubmit}>
        <Input
          value={`${inputValue}`}
          size="small"
          centered
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          data-testid="current.page"
        />
      </S.InputForm>
      <span data-testid="Pagination.totalPages">{`of ${pageCount}`}</span>
      <S.IconContainer
        $right
        $disabled={currentPage === pageCount}
        onClick={handleIconClick(currentPage + 1)}
        data-testid="Pagination.RightIcon"
      >
        <Icon component={ArrowShortRightIcon} height={16} />
      </S.IconContainer>
    </S.Pagination>
  )
}
